(function (root) {
  'use strict';

  const COMMON_CORRECTIONS = Object.freeze({
    antena: 'antenna',
    antanna: 'antenna',
    anntena: 'antenna',
    balen: 'balun',
    chamaleon: 'chameleon',
    chamelean: 'chameleon',
    chamelion: 'chameleon',
    chamilion: 'chameleon',
    commmon: 'common',
    comunication: 'communication',
    comunications: 'communications',
    counterpose: 'counterpoise',
    counterpoisse: 'counterpoise',
    dopler: 'doppler',
    emergancy: 'emergency',
    emcom: 'emcomm',
    groundng: 'grounding',
    ladar: 'ladder',
    lighning: 'lightning',
    annalizer: 'analyzer',
    patern: 'pattern',
    polerization: 'polarization',
    powr: 'power',
    protecton: 'protection',
    propogation: 'propagation',
    radails: 'radials',
    repeeter: 'repeater',
    ribakov: 'rybakov',
    rybacov: 'rybakov',
    rybakoff: 'rybakov',
    rybkov: 'rybakov',
    satelite: 'satellite',
    trubleshooting: 'troubleshooting',
    tunr: 'tuner',
    curent: 'current'
  });

  const KNOWN_COMPOUNDS = new Set(['tacyagi', 'urt1']);

  const QUERY_STOP_WORDS = new Set([
    'a', 'an', 'and', 'about', 'for', 'how', 'i', 'is', 'my', 'of', 'on', 'the', 'to', 'what', 'with'
  ]);

  const normalize = value => (value || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const QUERY_ALIASES = Object.freeze({
    'cha pas 2 0 mil': 'cha mpas 2 0 mil wire',
    'cha td 2 0 hpd field wire family': 'cha hpd td 2 0 field wire',
    'coax cable': 'coaxial cable feed line',
    'coax feedline': 'coaxial cable feed line',
    'd link': 'link d',
    'cha d link': 'cha link d',
    'dlink': 'link d',
    'cha dlink': 'cha link d',
    'd link15': 'link d15',
    'cha d link15': 'cha link d15',
    'd link 15': 'link d15',
    'cha d link 15': 'cha link d15',
    'dlink15': 'link d15',
    'cha dlink15': 'cha link d15',
    'stinger': 'cha stinger kit permanent'
  });

  const canonicalizeQuery = value => {
    const normalized = normalize(value);
    return QUERY_ALIASES[normalized] || normalized;
  };

  const correctToken = token => COMMON_CORRECTIONS[token] || token;
  const tokenize = value => normalize(value)
    .split(/\s+/)
    .filter(Boolean)
    .map(correctToken);
  const compact = value => tokenize(value).join('');

  const boundedDamerauLevenshtein = (left, right, maximum) => {
    if (left === right) return 0;
    if (!left || !right) return Math.max(left.length, right.length);
    if (Math.abs(left.length - right.length) > maximum) return maximum + 1;

    let previousPrevious = null;
    let previous = Array.from({ length: right.length + 1 }, (_, index) => index);

    for (let row = 1; row <= left.length; row += 1) {
      const current = [row];
      let rowMinimum = current[0];
      for (let column = 1; column <= right.length; column += 1) {
        const substitution = previous[column - 1] + (left[row - 1] === right[column - 1] ? 0 : 1);
        let value = Math.min(
          previous[column] + 1,
          current[column - 1] + 1,
          substitution
        );
        if (
          previousPrevious && row > 1 && column > 1 &&
          left[row - 1] === right[column - 2] &&
          left[row - 2] === right[column - 1]
        ) value = Math.min(value, previousPrevious[column - 2] + 1);
        current[column] = value;
        rowMinimum = Math.min(rowMinimum, value);
      }
      if (rowMinimum > maximum) return maximum + 1;
      previousPrevious = previous;
      previous = current;
    }
    return previous[right.length];
  };

  const allowedDistance = token => {
    if (/^\d+$/.test(token) || token.length <= 3) return 0;
    if (token.length <= 7) return 1;
    return 2;
  };

  const tokenSimilarity = (queryToken, candidateToken) => {
    if (queryToken === candidateToken) return 1;
    if (queryToken.length >= 4 && candidateToken.length >= 4 && (
      candidateToken.startsWith(queryToken) || queryToken.startsWith(candidateToken)
    )) return 0.88;
    const maximum = Math.max(allowedDistance(queryToken), allowedDistance(candidateToken));
    if (!maximum) return 0;
    const distance = boundedDamerauLevenshtein(queryToken, candidateToken, maximum);
    return distance <= maximum ? 1 - (distance / Math.max(queryToken.length, candidateToken.length)) : 0;
  };

  const FIELD_WEIGHTS = Object.freeze({
    title: 34,
    keywords: 22,
    meta: 14,
    quick: 9,
    summary: 5,
    body: 2
  });

  const documentCache = new WeakMap();
  const prepare = item => {
    if (item && typeof item === 'object' && documentCache.has(item)) return documentCache.get(item);
    const document = {
      title: normalize(item && item.title),
      keywords: normalize(item && item.keywords),
      meta: normalize(`${item && item.category || ''} ${item && item.section || ''} ${item && item.handle || ''}`),
      quick: normalize(item && item.quick),
      summary: normalize(item && item.summary),
      body: normalize(item && item.body)
    };
    document.titleCompact = compact(document.title);
    document.all = normalize(Object.values(document).join(' '));
    document.allCompact = compact(document.all);
    document.tokens = Object.fromEntries(
      Object.keys(FIELD_WEIGHTS).map(field => [field, Array.from(new Set(tokenize(document[field])))])
    );
    if (item && typeof item === 'object') documentCache.set(item, document);
    return document;
  };

  const prepareQuery = value => {
    const normalized = canonicalizeQuery(value);
    const sourceTokens = tokenize(normalized);
    const tokens = [];
    for (let index = 0; index < sourceTokens.length; index += 1) {
      const pair = `${sourceTokens[index]}${sourceTokens[index + 1] || ''}`;
      if (KNOWN_COMPOUNDS.has(pair)) {
        tokens.push(pair);
        index += 1;
      } else tokens.push(sourceTokens[index]);
    }
    const meaningful = tokens.filter(token => !QUERY_STOP_WORDS.has(token));
    return {
      normalized,
      tokens: meaningful.length ? meaningful : tokens,
      compact: (meaningful.length ? meaningful : tokens).join('')
    };
  };

  const score = (item, value) => {
    const query = prepareQuery(value);
    if (!query.normalized || !query.tokens.length) return 0;
    const document = prepare(item);
    let points = 0;

    // A governed product gateway is the customer's primary route to the exact
    // yellow-highlighted handbook section. When the query is exactly the
    // official product name or its normal shorthand without the CHA prefix,
    // keep that gateway above supporting articles about the same product.
    if (item?.resourceType === 'gateway') {
      const officialTitle = document.title;
      const shortTitle = officialTitle.replace(/^cha\s+/, '');
      if (query.normalized === officialTitle || query.normalized === shortTitle) points += 1000;
    }

    if (document.title === query.normalized) points += 220;
    if (document.title.startsWith(query.normalized)) points += 125;
    if (document.title.includes(query.normalized)) points += 90;
    if (document.keywords.includes(query.normalized)) points += 55;
    if (document.meta.includes(query.normalized)) points += 38;
    if (document.quick.includes(query.normalized)) points += 22;
    if (document.summary.includes(query.normalized)) points += 12;
    if (document.body.includes(query.normalized)) points += 5;

    if (query.compact.length >= 4) {
      if (document.titleCompact.includes(query.compact)) points += 115;
      else if (document.allCompact.includes(query.compact)) points += 42;
      else {
        const maximum = query.compact.length >= 9 ? 2 : 1;
        const titleDistance = boundedDamerauLevenshtein(query.compact, document.titleCompact, maximum);
        if (titleDistance <= maximum) points += 80 - (titleDistance * 12);
      }
    }

    let matchedTokens = 0;
    query.tokens.forEach(queryToken => {
      let best = 0;
      Object.entries(FIELD_WEIGHTS).forEach(([field, weight]) => {
        document.tokens[field].forEach(candidateToken => {
          const similarity = tokenSimilarity(queryToken, candidateToken);
          best = Math.max(best, similarity * weight);
        });
      });
      if (best >= 7) {
        matchedTokens += 1;
        points += best;
      }
    });

    const coverage = matchedTokens / query.tokens.length;
    if (!matchedTokens || coverage < 0.5) return 0;
    if (query.tokens.length > 1 && matchedTokens < 2) return 0;
    points += coverage * 35;
    if (coverage === 1) points += 24;
    return Math.round(points * 100) / 100;
  };

  const search = (items, query, limit) => (Array.isArray(items) ? items : [])
    .map(item => ({ item, score: score(item, query) }))
    .filter(entry => entry.score > 0)
    .sort((left, right) => right.score - left.score || String(left.item.title || '').localeCompare(String(right.item.title || '')))
    .slice(0, Number.isFinite(limit) ? limit : 30);

  root.CKB_FORGIVING_SEARCH = Object.freeze({
    version: '1.6.0',
    normalize,
    canonicalizeQuery,
    tokenize,
    distance: boundedDamerauLevenshtein,
    score,
    search
  });
})(typeof window !== 'undefined' ? window : globalThis);
